// Base types
export interface BaseEntity {
  id: string
  created_at: string
  updated_at: string
}

// API Response types
export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  meta?: Record<string, any>
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  per_page: number
  last_page: number
  from: number
  to: number
}

export interface ApiError {
  message: string
  errors?: ValidationError[]
  status?: number
  code?: string
}

export interface ValidationError {
  field: string
  message: string
  code?: string
}

// Customer types
export interface Customer extends BaseEntity {
  name: string
  phone: string
  email?: string
  address?: Address
  tax_number?: string
  type: 'individual' | 'business'
  credit_limit?: number
  payment_terms?: number
  branch_id: string
  is_active: boolean
}

export interface Address {
  street: string
  city: string
  state: string
  postal_code: string
  country: string
}

// Product types
export interface Product extends BaseEntity {
  name: string
  code: string
  barcode?: string
  price: number
  unit: string
  category: ProductCategory
  tax_rate: number
  in_stock: boolean
  stock_quantity?: number
  image?: string
  description?: string
  branch_id: string
}

export interface ProductCategory extends BaseEntity {
  name: string
  description?: string
  parent_id?: string
}

// Invoice types
export interface Invoice extends BaseEntity {
  bond_num: string
  bond_date: string
  due_date: string
  customer: Customer
  line_items: LineItem[]
  payment_methods: PaymentMethod[]
  subtotal: number
  discount_amount: number
  tax_amount: number
  total: number
  status: InvoiceStatus
  notes?: string
  branch_id: string
  
  // ZATCA related fields
  zatca_compliant?: boolean
  zatca_qr_code?: string
  zatca_uuid?: string
  zatca_hash?: string
  zatca_reported_at?: string
  zatca_cleared_at?: string
  
  // File attachments
  attachments?: FileAttachment[]
  
  // Additional metadata
  currency: string
  exchange_rate?: number
  printed_at?: string
  sent_at?: string
  paid_at?: string
}

export type InvoiceStatus = 
  | 'draft' 
  | 'pending' 
  | 'sent' 
  | 'paid' 
  | 'partially_paid' 
  | 'overdue' 
  | 'cancelled'

export interface LineItem {
  id: string
  product: Product
  quantity: number
  unit_price: number
  discount?: number
  discount_type?: 'percentage' | 'fixed'
  tax_rate: number
  total: number
  notes?: string
}

export interface PaymentMethod {
  id: string
  type: 'cash' | 'card' | 'bank_transfer' | 'check' | 'credit'
  amount: number
  details?: {
    card_number?: string
    bank_account?: string
    check_number?: string
    due_date?: string
    reference?: string
  }
}

// Filter types
export interface InvoiceFilters {
  search?: string
  status?: InvoiceStatus | 'all'
  date_from?: string
  date_to?: string
  customer_ids?: string[]
  branch_ids?: string[]
  min_amount?: number
  max_amount?: number
}

export interface CustomerFilters {
  search?: string
  type?: 'individual' | 'business' | 'all'
  is_active?: boolean
}

export interface ProductFilters {
  search?: string
  category_id?: string
  in_stock?: boolean
  barcode?: string
}

// File types
export interface FileAttachment extends BaseEntity {
  name: string
  file_type: 'image' | 'document' | 'video' | 'audio' | 'archive' | 'other'
  size: number
  size_human: string
  url: string
  category: FileCategory
  can_preview: boolean
  preview_url?: string
  invoice_id?: string
}

export type FileCategory = 
  | 'receipt' 
  | 'contract' 
  | 'photo' 
  | 'document' 
  | 'invoice_copy' 
  | 'signature' 
  | 'other'

// ZATCA types
export interface ZatcaStatus {
  compliance: 'compliant' | 'non_compliant' | 'pending'
  qr_code?: string
  uuid?: string
  hash?: string
  reported_at?: string
  cleared_at?: string
  errors: ZatcaError[]
  warnings: ZatcaWarning[]
}

export interface ZatcaError {
  code: string
  message: string
  severity: 'error' | 'warning'
}

export interface ZatcaWarning {
  code: string
  message: string
}

// User and Auth types
export interface User extends BaseEntity {
  name: string
  email: string
  phone?: string
  role: UserRole
  permissions: string[]
  main_role: string
  branches: Branch[]
  active_branch_id: string
  is_active: boolean
}

export type UserRole = 'super_admin' | 'admin' | 'manager' | 'staff'

export interface Branch extends BaseEntity {
  name: string
  address: Address
  phone: string
  email?: string
  tax_number?: string
  is_active: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  token: string
  expires_at: string
}

// Statistics and Dashboard types
export interface DashboardStats {
  today_sales: number
  pending_invoices: number
  zatca_compliance_rate: number
  monthly_revenue: number
  recent_invoices: Invoice[]
  sales_chart_data: ChartDataPoint[]
}

export interface ChartDataPoint {
  date: string
  value: number
  label?: string
}

// Search and suggestions
export interface SearchSuggestion {
  id: string
  text: string
  category?: string
  icon?: string
  metadata?: Record<string, any>
}

// Request/Form types
export interface CreateInvoiceRequest {
  customer_id: string
  bond_date: string
  due_date: string
  line_items: Omit<LineItem, 'id' | 'product'>[]
  payment_methods?: Omit<PaymentMethod, 'id'>[]
  notes?: string
  currency: string
  exchange_rate?: number
}

export interface UpdateInvoiceRequest extends Partial<CreateInvoiceRequest> {
  status?: InvoiceStatus
}

export interface CreateCustomerRequest {
  name: string
  phone: string
  email?: string
  address?: Address
  tax_number?: string
  type: 'individual' | 'business'
  credit_limit?: number
  payment_terms?: number
}

export interface UpdateCustomerRequest extends Partial<CreateCustomerRequest> {
  is_active?: boolean
}

export interface UploadFileRequest {
  file: File
  category: FileCategory
  invoice_id?: string
}

// Utility types
export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  group?: string
}

export interface TableColumn<T> {
  key: keyof T
  title: string
  sortable?: boolean
  filterable?: boolean
  render?: (value: any, row: T) => React.ReactNode
  width?: string | number
  fixed?: 'left' | 'right'
}

export interface PaginationParams {
  page?: number
  per_page?: number
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
} 

// Column Configuration Types
export interface ColumnConfig {
  key: string
  title: string
  visible: boolean
  required: boolean
  order: number
  width?: string | number
  type: 'text' | 'number' | 'date' | 'select' | 'boolean' | 'currency' | 'percentage'
  validation?: {
    required?: boolean
    min?: number
    max?: number
    pattern?: string
  }
  options?: SelectOption[]
  description?: string
  category: 'header' | 'line_item' | 'payment' | 'address' | 'zatca'
}

export interface InvoiceColumnConfiguration {
  id: string
  name: string
  description?: string
  is_default: boolean
  is_active: boolean
  user_id?: string
  branch_id?: string
  created_at: string
  updated_at: string
  
  // Column configurations by category
  header_columns: ColumnConfig[]
  line_item_columns: ColumnConfig[]
  payment_columns: ColumnConfig[]
  address_columns: ColumnConfig[]
  zatca_columns: ColumnConfig[]
}

// Default column configurations based on the documentation
export const DEFAULT_INVOICE_COLUMNS: Record<string, ColumnConfig[]> = {
  header: [
    {
      key: 'payment_method_sales',
      title: 'Command/Operation Type',
      visible: true,
      required: true,
      order: 1,
      type: 'select',
      category: 'header',
      description: 'Top command selection (سداد نقدى بالكامل، سداد ذهب كسر، تسكير مبيعات)',
      options: [
        { value: '1', label: 'سداد نقدى بالكامل' },
        { value: '2', label: 'سداد ذهب كسر' },
        { value: '3', label: 'تسكير مبيعات' }
      ]
    },
    {
      key: 'bond_num',
      title: 'Invoice Number',
      visible: true,
      required: true,
      order: 2,
      type: 'text',
      category: 'header',
      description: 'Auto-generated bond number (رقم السند)'
    },
    {
      key: 'bond_date',
      title: 'Invoice Date',
      visible: true,
      required: true,
      order: 3,
      type: 'date',
      category: 'header',
      validation: { required: true }
    },
    {
      key: 'due_date',
      title: 'Due Date',
      visible: true,
      required: false,
      order: 4,
      type: 'date',
      category: 'header'
    },
    {
      key: 'payment_type',
      title: 'Payment Type',
      visible: true,
      required: true,
      order: 5,
      type: 'select',
      category: 'header',
      description: 'النوع: نقدا/اجل',
      options: [
        { value: '1', label: 'Cash (نقدا)' },
        { value: '2', label: 'Credit (اجل)' }
      ]
    },
    {
      key: 'selling_time',
      title: 'Sale Time',
      visible: false,
      required: false,
      order: 6,
      type: 'text',
      category: 'header',
      description: 'Sale time (التوقيت)'
    },
    {
      key: 'def_branch',
      title: 'Branch',
      visible: true,
      required: true,
      order: 7,
      type: 'select',
      category: 'header',
      description: 'Branch ID - auto-selected'
    },
    {
      key: 'staff_user_id',
      title: 'Seller/Staff',
      visible: true,
      required: true,
      order: 8,
      type: 'select',
      category: 'header',
      description: 'Seller/staff ID (البائع)'
    },
    {
      key: 'manual_bond_num',
      title: 'Manual Invoice Number',
      visible: false,
      required: false,
      order: 9,
      type: 'text',
      category: 'header',
      description: 'Manual invoice number (رقم السند اليدوي)'
    },
    {
      key: 'parent_account_guide_id',
      title: 'Customer Account ID',
      visible: true,
      required: true,
      order: 10,
      type: 'text',
      category: 'header',
      description: 'Customer account ID (رمز العميل)'
    },
    {
      key: 'client_name',
      title: 'Customer Name',
      visible: true,
      required: true,
      order: 11,
      type: 'text',
      category: 'header',
      description: 'Customer name (أسم العميل)'
    },
    {
      key: 'customer_phone',
      title: 'Customer Phone',
      visible: true,
      required: false,
      order: 12,
      type: 'text',
      category: 'header',
      description: 'Phone number (رقم الهاتف)'
    },
    {
      key: 'sales_state',
      title: 'Invoice Statement',
      visible: true,
      required: true,
      order: 13,
      type: 'text',
      category: 'header',
      description: 'Invoice statement/notes (البيان)'
    },
    {
      key: 'kayed_num',
      title: 'Accounting Entry Number',
      visible: false,
      required: false,
      order: 14,
      type: 'text',
      category: 'header',
      description: 'Accounting entry number (رقم القيد)'
    },
    {
      key: 'currency',
      title: 'Currency',
      visible: true,
      required: true,
      order: 15,
      type: 'select',
      category: 'header',
      options: [
        { value: 'SAR', label: 'Saudi Riyal (SAR)' },
        { value: 'USD', label: 'US Dollar (USD)' },
        { value: 'EUR', label: 'Euro (EUR)' }
      ]
    },
    {
      key: 'exchange_rate',
      title: 'Exchange Rate',
      visible: false,
      required: false,
      order: 16,
      type: 'number',
      category: 'header',
      validation: { min: 0 }
    },
    {
      key: 'notes',
      title: 'Notes',
      visible: true,
      required: false,
      order: 17,
      type: 'text',
      category: 'header'
    }
  ],
  line_items: [
    {
      key: 'visible_product_code',
      title: 'Product Code',
      visible: true,
      required: true,
      order: 1,
      width: 120,
      type: 'text',
      category: 'line_item',
      description: 'User-editable product code (رمز الصنف)'
    },
    {
      key: 'product_name',
      title: 'Product Name',
      visible: true,
      required: true,
      order: 2,
      width: 200,
      type: 'text',
      category: 'line_item',
      description: 'Product name (أسم الصنف)'
    },
    {
      key: 'quantity_num',
      title: 'Quantity Number',
      visible: true,
      required: true,
      order: 3,
      width: 80,
      type: 'number',
      category: 'line_item',
      description: 'Number of pieces (العدد)',
      validation: { min: 1 }
    },
    {
      key: 'insertion_num',
      title: 'Serial Number',
      visible: true,
      required: false,
      order: 4,
      width: 120,
      type: 'text',
      category: 'line_item',
      description: 'Serial/insertion number (رقم المسلسل)'
    },
    {
      key: 'carat',
      title: 'Carat/Purity',
      visible: true,
      required: false,
      order: 5,
      width: 80,
      type: 'text',
      category: 'line_item',
      description: 'Item carat/purity (العيار)'
    },
    {
      key: 'unit',
      title: 'Unit',
      visible: true,
      required: true,
      order: 6,
      width: 80,
      type: 'select',
      category: 'line_item',
      description: 'Unit (الوحدة)',
      options: [
        { value: '1', label: 'Gram' },
        { value: '2', label: 'Piece' },
        { value: '3', label: 'Kilogram' }
      ]
    },
    {
      key: 'quantity',
      title: 'Weight/Quantity',
      visible: true,
      required: true,
      order: 7,
      width: 100,
      type: 'number',
      category: 'line_item',
      description: 'Weight or quantity (الكمية/الوزن)',
      validation: { min: 0 }
    },
    {
      key: 'stone_weight',
      title: 'Stone Weight',
      visible: false,
      required: false,
      order: 8,
      width: 100,
      type: 'number',
      category: 'line_item',
      description: 'Stone weight deduction (وزن الحجر)',
      validation: { min: 0 }
    },
    {
      key: 'cost_center_id',
      title: 'Cost Center',
      visible: false,
      required: false,
      order: 9,
      width: 120,
      type: 'select',
      category: 'line_item',
      description: 'Cost center for accounting (مركز التكلفة)'
    },
    {
      key: 'selling_price',
      title: 'Unit Price',
      visible: true,
      required: true,
      order: 10,
      width: 100,
      type: 'currency',
      category: 'line_item',
      description: 'Unit selling price (سعر البيع)',
      validation: { min: 0 }
    },
    {
      key: 'salary',
      title: 'Labor Cost',
      visible: true,
      required: true,
      order: 11,
      width: 100,
      type: 'currency',
      category: 'line_item',
      description: 'Labor cost per unit (الأجرة)',
      validation: { min: 0 }
    },
    {
      key: 'total_salaries',
      title: 'Total Labor',
      visible: true,
      required: false,
      order: 12,
      width: 120,
      type: 'currency',
      category: 'line_item',
      description: 'Calculated total labor (إجمالي الأجور)'
    },
    {
      key: 'discount',
      title: 'Discount %',
      visible: true,
      required: false,
      order: 13,
      width: 80,
      type: 'percentage',
      category: 'line_item',
      description: 'Discount percentage (الخصم)',
      validation: { min: 0, max: 100 }
    },
    {
      key: 'discount_value',
      title: 'Discount Value',
      visible: true,
      required: false,
      order: 14,
      width: 120,
      type: 'currency',
      category: 'line_item',
      description: 'Calculated discount amount (قيمة الخصم)'
    },
    {
      key: 'vat',
      title: 'VAT %',
      visible: true,
      required: true,
      order: 15,
      width: 80,
      type: 'percentage',
      category: 'line_item',
      description: 'VAT percentage (الضريبة)',
      validation: { min: 0, max: 100 }
    },
    {
      key: 'total',
      title: 'Line Total',
      visible: true,
      required: false,
      order: 16,
      width: 120,
      type: 'currency',
      category: 'line_item',
      description: 'Final line total (الإجمالي)'
    }
  ],
  payments: [
    {
      key: 'payment_amount',
      title: 'Amount',
      visible: true,
      required: true,
      order: 1,
      width: 120,
      type: 'currency',
      category: 'payment',
      description: 'Payment amount (المبلغ)',
      validation: { min: 0 }
    },
    {
      key: 'payment_method',
      title: 'Payment Method',
      visible: true,
      required: true,
      order: 2,
      width: 150,
      type: 'select',
      category: 'payment',
      description: 'Payment method (طريقة الدفع)',
      options: [
        { value: '1', label: 'نقدا (Cash)' },
        { value: '2', label: 'فيزا (Visa)' },
        { value: '3', label: 'ماستر كارد (MasterCard)' },
        { value: '4', label: 'أمريكان اكسبريس (American Express)' },
        { value: '5', label: 'تحويل بنكي (Bank Transfer)' },
        { value: '6', label: 'محفظة إلكترونية (Digital Wallet)' },
        { value: '7', label: 'شيك (Check)' },
        { value: '8', label: 'ذهب كسر (Gold fragments)' }
      ]
    },
    {
      key: 'reference',
      title: 'Reference Number',
      visible: true,
      required: false,
      order: 3,
      width: 150,
      type: 'text',
      category: 'payment',
      description: 'Reference number (رقم المرجع)'
    },
    {
      key: 'currency_id',
      title: 'Currency',
      visible: true,
      required: true,
      order: 4,
      width: 100,
      type: 'select',
      category: 'payment',
      description: 'Payment currency (العملة)',
      options: [
        { value: '1', label: 'SAR' },
        { value: '2', label: 'USD' },
        { value: '3', label: 'EUR' }
      ]
    },
    {
      key: 'account_guide_id',
      title: 'Account',
      visible: true,
      required: false,
      order: 5,
      width: 150,
      type: 'select',
      category: 'payment',
      description: 'Account guide for bookkeeping (الحسابات)'
    }
  ],
  address: [
    {
      key: 'delivery_date',
      title: 'Delivery Date',
      visible: true,
      required: false,
      order: 1,
      type: 'date',
      category: 'address',
      description: 'Delivery date (تاريخ التسليم)'
    },
    {
      key: 'delivery_hour',
      title: 'Delivery Time',
      visible: true,
      required: false,
      order: 2,
      type: 'text',
      category: 'address',
      description: 'Delivery time (الوقت)'
    },
    {
      key: 'delivery_address',
      title: 'Delivery Address',
      visible: true,
      required: false,
      order: 3,
      type: 'text',
      category: 'address',
      description: 'Delivery address (عنوان التسليم)'
    },
    {
      key: 'invoice_address',
      title: 'Billing Address',
      visible: true,
      required: false,
      order: 4,
      type: 'text',
      category: 'address',
      description: 'Billing address (عنوان الفواتير)'
    },
    {
      key: 'street_name',
      title: 'Street Name',
      visible: true,
      required: false,
      order: 5,
      type: 'text',
      category: 'address',
      description: 'Street name (اسم الشارع)'
    },
    {
      key: 'building_number',
      title: 'Building Number',
      visible: true,
      required: false,
      order: 6,
      type: 'text',
      category: 'address',
      description: 'Building number (رقم المبنى)'
    },
    {
      key: 'city_subdivision_name',
      title: 'District',
      visible: true,
      required: false,
      order: 7,
      type: 'text',
      category: 'address',
      description: 'District name (اسم الحي)'
    },
    {
      key: 'city_name',
      title: 'City',
      visible: true,
      required: false,
      order: 8,
      type: 'text',
      category: 'address',
      description: 'City name (اسم المدينة)'
    },
    {
      key: 'postal_zone',
      title: 'Postal Code',
      visible: true,
      required: false,
      order: 9,
      type: 'text',
      category: 'address',
      description: 'Postal code (الرمز البريدى)'
    },
    {
      key: 'country_subentity',
      title: 'Region',
      visible: false,
      required: false,
      order: 10,
      type: 'text',
      category: 'address',
      description: 'Region name (اسم المنطقة)'
    },
    {
      key: 'country',
      title: 'Country',
      visible: false,
      required: false,
      order: 11,
      type: 'text',
      category: 'address',
      description: 'Country (البلد)'
    },
    {
      key: 'alt_postal_code',
      title: 'Alt Postal Code',
      visible: false,
      required: false,
      order: 12,
      type: 'text',
      category: 'address',
      description: 'Additional postal code (رمز بريدي إضافي)'
    },
    {
      key: 'alt_address',
      title: 'Additional Address',
      visible: false,
      required: false,
      order: 13,
      type: 'text',
      category: 'address',
      description: 'Additional address (عنوان إضافى)'
    },
    {
      key: 'phone_num',
      title: 'Address Phone',
      visible: false,
      required: false,
      order: 14,
      type: 'text',
      category: 'address',
      description: 'Phone number (رقم الهاتف)'
    },
    {
      key: 'email',
      title: 'Address Email',
      visible: false,
      required: false,
      order: 15,
      type: 'text',
      category: 'address',
      description: 'Email address (عنوان البريد الإلكترونى)'
    }
  ],
  zatca: [
    {
      key: 'client_type',
      title: 'Customer Type',
      visible: true,
      required: true,
      order: 1,
      type: 'select',
      category: 'zatca',
      description: 'Customer type (نوع العميل)',
      options: [
        { value: 'individual', label: 'Individual (فرد)' },
        { value: 'company', label: 'Company (شركة)' },
        { value: 'organization', label: 'Organization (مؤسسة)' }
      ]
    },
    {
      key: 'identity_type',
      title: 'Identity Type',
      visible: true,
      required: true,
      order: 2,
      type: 'select',
      category: 'zatca',
      description: 'Identity type (نوع الهوية)',
      options: [
        { value: 'national_id', label: 'National ID (هوية وطنية)' },
        { value: 'iqama', label: 'Iqama (إقامة)' },
        { value: 'passport', label: 'Passport (جواز سفر)' },
        { value: 'commercial_register', label: 'Commercial Register (سجل تجاري)' },
        { value: 'tax_number', label: 'Tax Number (رقم ضريبي)' }
      ]
    },
    {
      key: 'tax_or_identity_number',
      title: 'Tax/ID Number',
      visible: true,
      required: true,
      order: 3,
      type: 'text',
      category: 'zatca',
      description: 'Tax/ID number (الرقم الضريبى/رقم الهوية)',
      validation: { required: true, pattern: '^(\\d{10}|\\d{15})$' }
    },
    {
      key: 'zatca_qr_code',
      title: 'QR Code',
      visible: true,
      required: false,
      order: 4,
      type: 'text',
      category: 'zatca',
      description: 'Generated ZATCA QR code'
    },
    {
      key: 'zatca_uuid',
      title: 'ZATCA UUID',
      visible: false,
      required: false,
      order: 5,
      type: 'text',
      category: 'zatca',
      description: 'ZATCA unique identifier'
    },
    {
      key: 'zatca_reported',
      title: 'ZATCA Reported',
      visible: true,
      required: false,
      order: 6,
      type: 'boolean',
      category: 'zatca',
      description: 'Whether invoice has been reported to ZATCA'
    },
    {
      key: 'zatca_hash',
      title: 'ZATCA Hash',
      visible: false,
      required: false,
      order: 7,
      type: 'text',
      category: 'zatca',
      description: 'ZATCA cryptographic hash'
    },
    {
      key: 'zatca_reported_at',
      title: 'ZATCA Reported At',
      visible: false,
      required: false,
      order: 8,
      type: 'date',
      category: 'zatca',
      description: 'Date when reported to ZATCA'
    },
    {
      key: 'zatca_cleared_at',
      title: 'ZATCA Cleared At',
      visible: false,
      required: false,
      order: 9,
      type: 'date',
      category: 'zatca',
      description: 'Date when cleared by ZATCA'
    }
  ]
}

// Request types for column configuration
export interface CreateColumnConfigRequest {
  name: string
  description?: string
  is_default?: boolean
  branch_id?: string
  header_columns: ColumnConfig[]
  line_item_columns: ColumnConfig[]
  payment_columns: ColumnConfig[]
  address_columns: ColumnConfig[]
  zatca_columns: ColumnConfig[]
}

export interface UpdateColumnConfigRequest {
  name?: string
  description?: string
  is_default?: boolean
  branch_id?: string
  header_columns?: ColumnConfig[]
  line_item_columns?: ColumnConfig[]
  payment_columns?: ColumnConfig[]
  address_columns?: ColumnConfig[]
  zatca_columns?: ColumnConfig[]
} 